export type GraphicsQuality = 'high' | 'medium' | 'low';

/** Measure delivered frames rather than guessing performance from device names. */
export const frameHealth = (intervals: number[]) => {
  const elapsed = intervals.reduce((total, interval) => total + interval, 0);
  return {
    fps: elapsed ? intervals.length * 1000 / elapsed : 60,
    slowRatio: intervals.length ? intervals.filter((interval) => interval > 22).length / intervals.length : 0,
  };
};

export const nextQuality = (quality: GraphicsQuality, intervals: number[], previousBadWindows: number) => {
  const { fps, slowRatio } = frameHealth(intervals);
  const bad = fps < (quality === 'high' ? 48 : 42) && slowRatio > 0.2;
  const badWindows = bad ? previousBadWindows + 1 : 0;
  const downgrade = bad && (fps < 30 || badWindows >= 2);
  return {
    quality: downgrade ? (quality === 'high' ? 'medium' : 'low') as GraphicsQuality : quality,
    badWindows: downgrade ? 0 : badWindows,
    fps,
  };
};

const decorativeNames = new Set(['atmosphere-drift', 'spore-float', 'darkness-dance', 'player-jump', 'emerald-rise', 'emerald-sparkle', 'online-beacon', 'offline-beacon']);

/** Ongoing interactions (navigation, arm poses, address morph, checkout) are never cancelled. */
const retireDecorations = (quality: GraphicsQuality) => {
  const releases: Array<() => void> = [];
  document.getAnimations().forEach((animation) => {
    const name = (animation as CSSAnimation).animationName;
    const effect = animation.effect as KeyframeEffect | null;
    const element = effect?.target;
    if (!name || !decorativeNames.has(name) || !(element instanceof Element) || !effect || animation.playState !== 'running') return;
    if (quality === 'medium' && !['atmosphere-drift', 'darkness-dance'].includes(name) && !(name === 'spore-float' && element.matches('.hero-spore:nth-child(n+13)'))) return;
    const tokens = new Set((element.getAttribute('data-adaptive-running') || '').split(' ').filter(Boolean));
    // An earlier downgrade may already be finishing this animation.
    if (tokens.has(name)) return;
    tokens.add(name);
    element.setAttribute('data-adaptive-running', Array.from(tokens).join(' '));
    const release = () => {
      const remaining = (element.getAttribute('data-adaptive-running') || '').split(' ').filter((token) => token && token !== name);
      if (remaining.length) element.setAttribute('data-adaptive-running', remaining.join(' '));
      else element.removeAttribute('data-adaptive-running');
    };
    const timing = effect.getComputedTiming();
    if (typeof timing.duration === 'number' && timing.duration <= 5000) {
      // Land at the next cycle boundary (e.g. players return to the ground).
      effect.updateTiming({ iterations: Number(timing.currentIteration ?? 0) + 1 });
      animation.finished.then(release, release);
    } else {
      // Long ambient loops fade out instead of freezing halfway through a movement.
      const pseudoElement = (effect as KeyframeEffect & { pseudoElement?: string | null }).pseudoElement;
      const opacity = getComputedStyle(element, pseudoElement || null).opacity;
      const fade = element.animate([{ opacity }, { opacity: 0 }], {
        duration: 280, easing: 'ease-out', fill: 'forwards', pseudoElement: pseudoElement || undefined,
      } as KeyframeAnimationOptions);
      fade.finished.then(() => { animation.cancel(); release(); fade.cancel(); }, release);
    }
    releases.push(release);
  });
  return releases;
};

/** One session per page load. No storage, benchmark animation, or React renders per frame. */
export const startAdaptiveGraphics = () => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let quality: GraphicsQuality = reducedMotion.matches ? 'low' : 'high';
  let previous = 0;
  let intervals: number[] = [];
  let elapsed = 0;
  let badWindows = 0;
  let frame = 0;
  const releases: Array<() => void> = [];
  root.dataset.graphicsQuality = quality;

  const apply = (next: GraphicsQuality) => {
    if (quality === next) return;
    // Mark current loops before CSS disables new loops at the lower quality.
    releases.push(...retireDecorations(next));
    quality = next;
    root.dataset.graphicsQuality = next;
  };
  const resetSample = () => { previous = 0; intervals = []; elapsed = 0; badWindows = 0; };
  const sample = (now: number) => {
    if (previous) {
      const interval = now - previous;
      intervals.push(interval);
      elapsed += interval;
      if (elapsed >= 450) {
        const result = nextQuality(quality, intervals, badWindows);
        badWindows = result.badWindows;
        root.dataset.graphicsFps = String(Math.round(result.fps));
        apply(result.quality);
        intervals = []; elapsed = 0;
      }
    }
    previous = now;
    if (quality !== 'low' && !document.hidden) frame = requestAnimationFrame(sample);
  };
  const visibility = () => {
    cancelAnimationFrame(frame);
    resetSample();
    if (!document.hidden && quality !== 'low') frame = requestAnimationFrame(sample);
  };
  const preference = () => {
    if (reducedMotion.matches) { apply('low'); cancelAnimationFrame(frame); }
  };
  document.addEventListener('visibilitychange', visibility);
  reducedMotion.addEventListener('change', preference);
  if (!document.hidden && quality !== 'low') frame = requestAnimationFrame(sample);
  return () => {
    cancelAnimationFrame(frame);
    document.removeEventListener('visibilitychange', visibility);
    reducedMotion.removeEventListener('change', preference);
    releases.forEach((release) => release());
  };
};
